import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Home, Loader2, XCircle } from "lucide-react";
import { useLang } from "@/i18n";
import { Logo } from "@/components/Logo";
import { API_BASE as API } from "@/config";

export default function PaymentResult({ kind }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(kind === "cancel" ? "cancelled" : "pending");
  const tries = useRef(0);

  useEffect(() => {
    if (kind !== "success" || !sessionId) return;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {
        /* keep polling */
      }
      tries.current += 1;
      if (tries.current < 12) setTimeout(poll, 2000);
      else setStatus("failed");
    };
    poll();
  }, [kind, sessionId]);

  const copy = {
    paid: { icon: CheckCircle2, cls: "text-emerald-400", title: t("pay_status_success"), desc: t("pay_status_success_desc") },
    pending: { icon: Loader2, cls: "text-blue-400 animate-spin", title: t("pay_status_pending"), desc: "" },
    failed: { icon: XCircle, cls: "text-red-400", title: t("pay_status_failed"), desc: t("pay_status_failed_desc") },
    cancelled: { icon: XCircle, cls: "text-slate-400", title: t("pay_status_cancel"), desc: t("pay_status_cancel_desc") },
  }[status];
  const Icon = copy.icon;

  return (
    <div className="min-h-screen bg-[#060B14] grid-lines flex items-center justify-center px-4" data-testid="payment-result-page">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-3xl border border-blue-500/20 bg-[#0B1526] p-10 text-center shadow-[0_30px_80px_rgba(2,6,17,0.6)]"
      >
        <div className="flex justify-center mb-8"><Logo compact /></div>
        <Icon size={56} className={`mx-auto ${copy.cls}`} data-testid="payment-status-icon" />
        <h1 className="mt-6 text-2xl font-extrabold text-white" data-testid="payment-status-title">{copy.title}</h1>
        {copy.desc && <p className="mt-3 text-sm leading-relaxed text-slate-400">{copy.desc}</p>}
        {sessionId && status === "paid" && (
          <div className="mt-5 rounded-xl bg-blue-950/40 border border-blue-500/20 p-3" data-testid="payment-reference">
            <span className="text-xs text-slate-400">{t("pay_ref")}: </span>
            <span className="font-mono-tag text-xs text-blue-300 break-all" dir="ltr">{sessionId}</span>
          </div>
        )}
        <Link
          data-testid="payment-back-home-button"
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-bold text-white hover:bg-blue-500 transition-colors"
        >
          <Home size={15} />
          {t("pay_back")}
        </Link>
      </motion.div>
    </div>
  );
}
